import React, { useCallback, useEffect, useState } from "react";
import { ArrowRight, ClipboardList, Gauge, LayoutDashboard, LogOut } from "lucide-react";
import { API_URL, ENABLE_DSR } from "../config";
import { apiRequest } from "../apiRequest";
import ArrTicker from "../components/ArrTicker";
import { ARR_REFRESH_INTERVAL_MS, getStoredArrPeriod } from "../utils/arr";

const MODULES = [
  {
    id: "sales",
    title: "Sales Dashboard",
    description: "Live floor status, ARR performance and operational controls.",
    icon: LayoutDashboard
  },
  {
    id: "kpi",
    title: "KPI Dashboard",
    description: "Daily KPI records, monthly results, quarterly performance and reporting.",
    icon: Gauge
  },
  {
    id: "dsr",
    title: "DSR",
    description: "Daily Sales Reports, reviews, approvals and reporting.",
    icon: ClipboardList,
    disabled: !ENABLE_DSR
  }
];

export default function AdminHome({ user, onSelect, onLogout, kpiSsoError = "" }) {
  const demoMode = process.env.REACT_APP_DEMO_MODE === "true";
  const [arrSummary, setArrSummary] = useState(null);
  const [arrLoading, setArrLoading] = useState(true);
  const [arrError, setArrError] = useState(false);

  const refreshArr = useCallback(async () => {
    const { month, year } = getStoredArrPeriod();
    const params = new URLSearchParams({ month, year });
    params.set("_t", String(Date.now()));

    setArrLoading(true);
    try {
      const { data } = await apiRequest(`${API_URL}/arr-leaderboard?${params.toString()}`, {
        cache: "no-store",
        headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` }
      }, { retries: 0 });
      if (!data?.summary) throw new Error("ARR summary is missing");
      setArrSummary(data.summary);
      setArrError(false);
    } catch {
      setArrError(true);
    } finally {
      setArrLoading(false);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      if (document.visibilityState === "hidden") return;
      await refreshArr();
    };
    load();
    const interval = setInterval(load, ARR_REFRESH_INTERVAL_MS);
    document.addEventListener("visibilitychange", load);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", load);
    };
  }, [refreshArr]);

  return (
    <main className="admin-home">
      <div className="admin-home-content">
        <header className="admin-home-header">
          <img src="/truckx-logo.webp" alt="TruckX" />
          <div className="admin-home-header-copy">
            <span className="admin-home-eyebrow">Sales Floor</span>
            <h1>Welcome{user?.name ? `, ${user.name}` : ""}</h1>
            <p>Choose a workspace</p>
            {demoMode && <span className="admin-home-demo-badge">DEMO DATA</span>}
          </div>
          <div className="admin-home-header-actions">
            <button className="admin-home-logout" type="button" onClick={onLogout} aria-label="Logout" title="Logout">
              <LogOut size={17} aria-hidden="true" /> <span>Logout</span>
            </button>
          </div>
        </header>

        <ArrTicker summary={arrSummary} loading={arrLoading} error={arrError} home />

        <section className="admin-home-workspaces" aria-labelledby="workspaces-title">
          <h2 id="workspaces-title">Workspaces</h2>
          <nav className="admin-home-modules" aria-label="Workspaces">
            {MODULES.map(({ id, title, description, icon: Icon, disabled }) => (
              <button
                className={`admin-home-module admin-home-module-${id}`}
                key={id}
                type="button"
                disabled={disabled}
                aria-label={`${title}. ${disabled ? "DSR is currently unavailable." : description}`}
                onClick={() => onSelect(id)}
              >
                <span className="admin-home-module-icon"><Icon aria-hidden="true" size={22} /></span>
                <span className="admin-home-module-copy">
                  <strong>{title}</strong>
                  <small>{disabled ? "DSR is currently unavailable." : description}</small>
                </span>
                <ArrowRight className="admin-home-module-arrow" aria-hidden="true" size={18} />
              </button>
            ))}
          </nav>
          {kpiSsoError && <p className="admin-home-kpi-sso-error" role="alert">{kpiSsoError}</p>}
        </section>
      </div>
    </main>
  );
}
